import { GridusException } from '../exceptions/gridusException';
import { get } from './fetch';

const encoder = new TextEncoder();
const BLOCK_SIZE = 16;
let pubKey: string | null = null;

async function fetchPublicKey(): Promise<boolean> {
  const response = await get<{ data: string }>('/auth/key');
  // if failed to fetch public key, throw an error
  if (response.status === 'error') {
    throw new GridusException(response.message);
  }

  const { data: publicKey } = response;
  pubKey = publicKey;

  return !!pubKey;
}

async function importPublicKey(pemKey: string) {
  const binaryDer = base64ToUint8Array(pemKey);

  return await crypto.subtle.importKey(
    'spki',
    binaryDer,
    {
      name: 'RSA-OAEP',
      hash: { name: 'SHA-256' },
    },
    true,
    ['encrypt']
  );
}

function strToUint8Array(str: string) {
  const buf = new ArrayBuffer(str.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < str.length; i++) {
    view[i] = str.charCodeAt(i);
  }
  return buf;
}

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const uint8Array = new Uint8Array(buffer);
  let binaryString = '';
  uint8Array.forEach((byte) => {
    binaryString += String.fromCharCode(byte);
  });
  return btoa(binaryString);
};

const base64ToUint8Array = (base64: string) => {
  const binaryString = atob(base64);
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }

  return uint8Array;
};

const generateSymmetricKey = async () => {
  const key = await crypto.subtle.generateKey(
    {
      name: 'AES-CBC',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  );

  const exportedKey = await crypto.subtle.exportKey('raw', key);
  const base64Key = arrayBufferToBase64(exportedKey);

  return base64Key;
};

const asymmetricEncrypt = async (
  plaintext: string,
  inputEncoding: BufferEncoding = 'base64',
  outputEncoding: BufferEncoding = 'base64'
) => {
  // 호출구간에서 pubKey null 여부 처리하기 때문에 !처리
  const key = await importPublicKey(pubKey!);
  let dataBuffer: ArrayBuffer;

  if (inputEncoding === 'base64') {
    dataBuffer = base64ToUint8Array(plaintext);
  } else {
    dataBuffer = strToUint8Array(plaintext);
  }

  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    key,
    dataBuffer
  );

  let result;
  if (outputEncoding === 'base64') {
    result = arrayBufferToBase64(encryptedData);
  } else {
    const decoder = new TextDecoder('utf-8');
    const decodedString = decoder.decode(encryptedData);
    result = decodedString;
  }

  return result;
};

const encryptAES = async (plaintext: string, key: string, iv: string) => {
  const plaintextBuffer = encoder.encode(plaintext);
  const keyBuffer = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
  const ivBuffer = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0));

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: 'AES-CBC' },
    false,
    ['encrypt']
  );

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv: ivBuffer,
    },
    cryptoKey,
    plaintextBuffer
  );

  const encryptedText = btoa(
    String.fromCharCode(...new Uint8Array(encryptedBuffer))
  );

  return encryptedText;
};

const generateRandomIVBase64 = (length: number = 12): string => {
  const iv = new Uint8Array(length);
  crypto.getRandomValues(iv);

  return btoa(String.fromCharCode(...iv));
};

const encryptData = async <T>(
  data: string,
  encryptedBodyKey: string
): Promise<Record<string, string | T>> => {
  // public key 1번 fetch할 수 있도록 처리 하는 부분
  if (!pubKey) {
    if (!(await fetchPublicKey()))
      throw new GridusException('Public key is not loaded.');
  }

  const iv = generateRandomIVBase64(BLOCK_SIZE);
  const key = await generateSymmetricKey();
  const encryptedBody = await encryptAES(data, key, iv);
  const encryptedKey = await asymmetricEncrypt(key);
  const requestBody = {
    key: encryptedKey,
    iv,
    [encryptedBodyKey]: encryptedBody,
  };

  return requestBody;
};

export { encryptData };
